import { useCallback, useRef, useState, type ReactNode } from "react";
import { Animated, Easing, View } from "react-native";

type DismissibleSpotlightStackProps<T> = {
  items: T[];
  keyExtractor: (item: T) => string;
  renderItem: (item: T, dismiss: () => void) => ReactNode;
  onDismiss: (item: T) => void;
  gap?: number;
  topSpacing?: number;
};

export function DismissibleSpotlightStack<T>({
  items,
  keyExtractor,
  renderItem,
  onDismiss,
  gap = 10,
  topSpacing = 12,
}: DismissibleSpotlightStackProps<T>) {
  if (items.length === 0) return null;

  return (
    <View style={{ marginTop: topSpacing }}>
      {items.map((item, index) => (
        <DismissibleSpotlightFrame
          key={keyExtractor(item)}
          item={item}
          isLast={index === items.length - 1}
          gap={gap}
          onDismiss={onDismiss}
          renderItem={renderItem}
        />
      ))}
    </View>
  );
}

function DismissibleSpotlightFrame<T>({
  item,
  isLast,
  gap,
  onDismiss,
  renderItem,
}: {
  item: T;
  isLast: boolean;
  gap: number;
  onDismiss: (item: T) => void;
  renderItem: (item: T, dismiss: () => void) => ReactNode;
}) {
  const animation = useRef(new Animated.Value(1)).current;
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);

  const handleDismiss = useCallback(() => {
    if (isDismissing) return;
    setIsDismissing(true);

    Animated.timing(animation, {
      toValue: 0,
      duration: 240,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (finished) {
        onDismiss(item);
      }
    });
  }, [animation, isDismissing, item, onDismiss]);

  const animatedHeight =
    isDismissing && measuredHeight !== null
      ? animation.interpolate({
          inputRange: [0, 1],
          outputRange: [0, measuredHeight],
        })
      : undefined;
  const animatedMargin =
    isDismissing && !isLast
      ? animation.interpolate({
          inputRange: [0, 1],
          outputRange: [0, gap],
        })
      : isLast
        ? 0
        : gap;

  return (
    <Animated.View
      onLayout={(event) => {
        if (!isDismissing) {
          setMeasuredHeight(event.nativeEvent.layout.height);
        }
      }}
      style={{
        height: animatedHeight,
        marginBottom: animatedMargin,
        opacity: animation,
        overflow: isDismissing ? "hidden" : "visible",
        transform: [
          {
            translateY: animation.interpolate({
              inputRange: [0, 1],
              outputRange: [-8, 0],
            }),
          },
          {
            scale: animation.interpolate({
              inputRange: [0, 1],
              outputRange: [0.985, 1],
            }),
          },
        ],
      }}
    >
      {renderItem(item, handleDismiss)}
    </Animated.View>
  );
}
